"use client";

import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ExpandedState,
  type FilterFn,
  type FilterFns,
  type PaginationState,
  type Row,
  type SortingState,
  type Table as TTable,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Input } from "./Input";
import { Button } from "./Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { TagIcon, TriangleAlert } from "lucide-react";

import { isAfter, isBefore, isSameDay, isValid, parse } from "date-fns";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./Dialog";

import { cn } from "@repo/shared/utils";
import { DataTableColumnsDropdown } from "./DataTableColumnsDropdown";
import { dataTableReverseContext } from "./dataTableReverseContext";

export interface TableLayoutState {
  pagination?: Omit<PaginationState, "pageIndex">;
  sorting?: SortingState;
  columnFilters?: ColumnFiltersState;
  columnVisibility?: VisibilityState;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];

  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactElement;
  filterColumn?: {
    label: string;
    columnName: string;
  };
  hasTags?: boolean;
  includeMainFilter?: boolean;
  tagFilterColumn?: {
    label: string;
    columnName: string;
  };

  fixedHeader?: boolean;

  userDefaults?: TableLayoutState;
  factoryDefaults?: TableLayoutState;

  onLayoutSave?: (state: TableLayoutState) => void;
  onLayoutReset?: () => void;
  layoutSaveStrategy?: "automatic" | "user";
  getRowCanExpand?: (row: Row<TData>) => boolean;
  onRowClick?: (row: Row<TData>) => void;
}

// #region Filter Funcs

const dateFilter = <TData,>(
  row: Row<TData>,
  columnId: string,
  value: [string, string]
) => {
  const refDate = row.getValue(columnId) as Date;

  const ll = !!value[0] && parse(value[0], "yyyy-MM-dd", new Date());
  const ul = !!value[1] && parse(value[1], "yyyy-MM-dd", new Date());

  const passes = [true, true];

  if (isValid(ul)) {
    passes[1] = isBefore(refDate, ul as Date) || isSameDay(refDate, ul as Date);
  }

  if (isValid(ll)) {
    passes[0] = isAfter(refDate, ll as Date) || isSameDay(refDate, ll as Date);
  }

  return passes.every((v) => v);
};

const makeAsyncFilter = <TData,>(table: TTable<TData>): FilterFn<TData> => {
  // debounced filter refresh method
  let timer = -1;
  const refresh = () => {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      table.setColumnFilters((v) => v);
    }, 50);
  };

  const columnState = {} as Record<
    string,
    {
      filtered: WeakMap<Row<TData>, { show: boolean; lastValue: unknown }>;
      state: object;
    }
  >;

  // async-to-sync filter converter
  return (row, columnId, filterValue) => {
    // get the meta-passed async filter method
    const { asyncFilterFn } = table.getColumn(columnId)?.columnDef.meta ?? {};

    // get state for this column
    columnState[columnId] ??= {
      filtered: new WeakMap(), // preserve row filter state
      state: {}, // per-column async filter method's custom state
    };
    const { filtered, state } = columnState[columnId];

    // lastValue == empty object: unique so it never matches filterValue to start
    const filterState = filtered.get(row) || { show: true, lastValue: {} };
    filtered.set(row, filterState);

    // filter changed since this row was last tested; re-run test
    if (filterState.lastValue !== filterValue) {
      filterState.lastValue = filterValue;
      asyncFilterFn?.(row, columnId, filterValue, table, state).then(
        (value) => {
          // if filterValue changed during load, then bail and wait for next async call to resolve
          if (filterState.lastValue !== filterValue) return;

          filterState.show = value; // async complete; assign resolved value
          refresh(); // refresh to trigger filter update
        }
      );
    }

    // in general, return the last test result of the filter for this row
    return filterState.show;
  };
};

// #endregion

// #region DataTable

export const DataTable = <TData, TValue>({
  columns,
  data,
  renderSubComponent,
  filterColumn = { label: "name", columnName: "name" },
  hasTags = false,
  includeMainFilter = true,
  tagFilterColumn = { label: "tags", columnName: "tags" },
  fixedHeader = true,
  userDefaults = {
    pagination: { pageSize: 10 },
    sorting: [],
    columnFilters: [],
    columnVisibility: { id: false },
  },
  factoryDefaults = {
    pagination: { pageSize: 10 },
    sorting: [],
    columnFilters: [],
    columnVisibility: { id: false },
  },
  onLayoutSave,
  onLayoutReset,
  layoutSaveStrategy = "user",
  getRowCanExpand,
  onRowClick,
}: DataTableProps<TData, TValue>) => {
  // table layout state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: userDefaults.pagination?.pageSize ?? 10,
  });
  const [sorting, setSorting] = useState<SortingState>(
    userDefaults.sorting ?? []
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    userDefaults.columnFilters ?? []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    userDefaults.columnVisibility ?? {}
  );

  // non-layout state
  const [rowSelection, setRowSelection] = useState({});
  const [expanded, setExpanded] = useState<ExpandedState>({});

  // if layout state changes, trigger onLayoutChange
  useEffect(() => {
    if (layoutSaveStrategy === "automatic") {
      const { pageSize } = pagination;

      onLayoutSave?.({
        pagination: { pageSize },
        sorting,
        columnVisibility,
        columnFilters,
      });
    }
  }, [
    pagination,
    sorting,
    columnFilters,
    columnVisibility,
    layoutSaveStrategy,
    onLayoutSave,
  ]);

  // prepare filter functions
  const filterFns: FilterFns = {
    date: dateFilter,
    async: () => true, // placeholder
  };

  // #region (TnStk Table)

  const table = useReactTable({
    data,
    columns,
    filterFns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    getRowCanExpand,
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedRowModel: getFacetedRowModel(), // client-side faceting
    getFacetedUniqueValues: getFacetedUniqueValues(), // generate unique values for select filter/autocomplete
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    onPaginationChange: setPagination,

    state: {
      expanded,
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  });

  // implementation for async filters
  // (MUST occur before reverse context pass-up)
  filterFns.async = useMemo(
    () => makeAsyncFilter<unknown>(table as TTable<unknown>),
    [table]
  );

  // pass up filtered row data
  const tableData = table.getFilteredRowModel().rows.map((row) => row.original);
  const visibleFields = table
    .getVisibleLeafColumns()
    .map((column) => column.id);
  dataTableReverseContext.useAsChild({ data: tableData, visibleFields });

  const hasHiddenFilters = table
    .getAllColumns()
    .some((column) => column.getIsFiltered() && !column.getIsVisible());

  // #region (JSX)

  const columnSettings = (
    <DataTableColumnsDropdown
      full={!fixedHeader}
      table={table}
      layoutSaveStrategy={layoutSaveStrategy}
      onSave={
        onLayoutSave &&
        (() => {
          onLayoutSave({
            pagination: { pageSize: pagination.pageSize },
            sorting,
            columnVisibility,
            columnFilters,
          });
        })
      }
      onRestore={() => {
        setSorting(userDefaults.sorting ?? []);
        setColumnFilters(userDefaults.columnFilters ?? []);
        setColumnVisibility(userDefaults.columnVisibility ?? {});
      }}
      onReset={
        onLayoutReset &&
        (() => {
          setSorting(factoryDefaults.sorting ?? []);
          setColumnFilters(factoryDefaults.columnFilters ?? []);
          setColumnVisibility(factoryDefaults.columnVisibility ?? {});
          // onLayoutReset is never called.. because calling it would not only revert to but also save the factory state
          // it should likely be a boolean, but perhaps leave in case saving factory state on reset is desired in the future (?)
        })
      }
    />
  );

  return (
    <div className="flex flex-col flex-grow">
      {!fixedHeader || includeMainFilter || hasTags ? (
        <div className="flex items-center py-4">
          {includeMainFilter && (
            <Input
              id="filterName"
              placeholder={`Filter by ${filterColumn.label}...`}
              value={
                (table
                  .getColumn(filterColumn.columnName)
                  ?.getFilterValue() as string) ?? ""
              }
              onChange={(event) =>
                table
                  .getColumn(filterColumn.columnName)
                  ?.setFilterValue(event.target.value)
              }
              containerStyle="max-w-sm rounded-sm"
            />
          )}
          {hasTags && (
            <>
              <div className="w-3" />
              <Input
                id="filterTags"
                placeholder={`Filter by ${tagFilterColumn.label}...`}
                icon={TagIcon}
                value={
                  (table
                    .getColumn(tagFilterColumn.columnName)
                    ?.getFilterValue() as string) ?? ""
                }
                onChange={(event) =>
                  table
                    .getColumn(tagFilterColumn.columnName)
                    ?.setFilterValue(event.target.value)
                }
                containerStyle="text-muted-foreground"
              />
            </>
          )}

          <div className="grow" />

          {hasHiddenFilters && (
            <Dialog>
              <DialogTrigger asChild>
                <Button className="bg-destructive/70 hover:bg-destructive/50 active:bg-destructive rounded-full w-8 h-8 text-xl">
                  <TriangleAlert className="text-white w-5 h-5 flex-shrink-0 -mt-0.5" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Hidden column with filter</DialogTitle>
                </DialogHeader>
                <DialogBody>
                  <p>
                    One or more hidden columns have a filter applied, which may
                    cause some rows to be hidden unexpectedly.
                  </p>
                  <p className="mt-2">
                    To find these columns, click 'Columns' to open up the list
                    of columns, and locate the columns with a filter icon to the
                    right that are not checked as visible.
                  </p>
                </DialogBody>
              </DialogContent>
            </Dialog>
          )}

          {!fixedHeader && columnSettings}
        </div>
      ) : (
        <div className="pb-4" />
      )}
      <div
        className={cn(
          "rounded-md border bg-white flex flex-col relative z-0",
          fixedHeader && "flex-grow"
        )}
      >
        {
          // #region (Table Head)
        }
        <Table
          wrapperClassName={cn("flex-grow", fixedHeader && "h-0")}
          inserts={{
            before: fixedHeader ? (
              <div className="sticky float-left -mb-96 top-0 left-0 z-20 p-2 bg-white">
                {columnSettings}
              </div>
            ) : null,
          }}
        >
          <TableHeader
            className={cn(
              "bg-white shadow-sm z-10",
              fixedHeader && "sticky top-0"
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="h-full hover:bg-white">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "text-foreground",
                        header.column.columnDef.meta?.className
                      )}
                    >
                      <div className="flex flex-row justify-start min-w-8 h-full align-middle">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          {
            // #region (Table Body)
          }

          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                return (
                  <Fragment key={row.id}>
                    <TableRow
                      className={
                        onRowClick ? "cursor-pointer clickable-row" : ""
                      }
                      onClick={(evt) => {
                        const { target } = evt;

                        // climb tree to find row node
                        if (target instanceof Element) {
                          let current: Element | null = target;
                          while (current) {
                            if (current.classList.contains("clickable-row")) {
                              break;
                            }
                            const tag = current.tagName.toLowerCase();
                            // if a form element is reached, don't trigger 'click'
                            if (
                              tag === "button" ||
                              tag === "input" ||
                              tag === "select" ||
                              tag === "textarea"
                            ) {
                              return;
                            }
                            current = current.parentElement;
                          }
                        }
                        onRowClick?.(row);
                      }}
                    >
                      {/* first row is a normal row */}
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {row.getIsExpanded() && renderSubComponent && (
                      <TableRow>
                        {/* 2nd row is a custom 1 cell row */}
                        {renderSubComponent({ row })}
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {
        // #region (Pagination)
      }

      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          Show{" "}
          <select
            value={table.getState().pagination.pageSize}
            className="p-1 rounded-md"
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>{" "}
          of {table.getFilteredRowModel().rows.length} rows.
        </div>

        <div className="flex items-center justify-end space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => table.firstPage()}
            disabled={!table.getCanPreviousPage()}
          >
            &laquo;
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            &#8249;
          </Button>
          <span>
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}.
          </span>
          <Button
            className="text-base"
            variant="ghost"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            &#8250;
          </Button>
          <Button
            className="text-base"
            variant="ghost"
            size="sm"
            onClick={() => table.lastPage()}
            disabled={!table.getCanNextPage()}
          >
            &raquo;
          </Button>
        </div>
      </div>
    </div>
  );
};

// #endregion

interface DataTableWrapperProps {
  children: React.ReactNode;
}

export const DataTableWrapper = ({ children }: DataTableWrapperProps) => (
  <div className="flex flex-grow flex-row gap-4">
    <div className="flex-grow w-0 flex flex-col">{children}</div>
  </div>
);
